import TenantComponents from '@components';
import tenantConstants from '@constants';
import { Radio, Typography } from 'antd';
import cx from 'clsx';
import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Alert, Card, ConfirmationModal, DataTable, Flex, Icon } from '../../../../../components/common';
import { CardCompact } from '../../../../../components/common/cards/styled';
import { formatPrice, resolveCreditsRequired } from '../../../../../utility/utility';
import { RadioStyled } from './styled';
import { isUnifiedProfolioEnabled } from '../../../../../utility/user';
import { canBuyQuota, getQuotaProductName } from '../../../../../utility/quota';
import { InsufficientCreditsAlertIcon, ListingExpiryUpdateIcon } from '../../../../../components/svg';
const { Text } = Typography;

const RadioGroup = Radio.Group;

export const QuotaCreditModal = forwardRef((props, ref) => {
  const {
    action,
    onSuccess,
    onCancel,
    serviceDataRef,
    showInsufficientCreditsAlert,
    showInfoMessage,
    hideDataTable = false,
    paymentOptions,
    selectedPaymentOption,
    onChangePaymentOption,
    unifiedCreditsOnly = false,
    ...rest
  } = props;
  const { isMobile, locale } = useSelector((state) => state.app.AppConfig);
  const { user } = useSelector((state) => state.app.loginUser);
  const isUnifiedPostListing = isUnifiedProfolioEnabled({ featureKey: 'postListing' });
  const { t } = useTranslation();
  const modalRef = useRef();

  useImperativeHandle(ref, () => ({
    showQuotaCreditModal() {
      serviceDataRef && serviceDataRef?.current && serviceDataRef?.current?.resetForm();
      modalRef && modalRef?.current && modalRef?.current?.showModal();
    },
    hideQuotaCreditModal() {
      modalRef && modalRef?.current && modalRef?.current?.hideModal();
    },
    setLoading(value) {
      modalRef && modalRef?.current && modalRef?.current?.setLoading(value);
    },
  }));

  const quotaColumns = (t) => {
    return [
      { title: t('Quota Required'), dataIndex: 'used', key: 'used' },
      { title: t('Quota Available'), dataIndex: 'available', key: 'available' },
    ];
  };

  const creditColumns = (t) => [
    { title: t('Credits Required'), dataIndex: 'used', key: 'used' },
    { title: t('Credits Available'), dataIndex: 'available', key: 'available' },
  ];

  const requestService = useMemo(
    () =>
      action?.applicableProduct?.slug == 'photography-service' ||
      action?.applicableProduct?.slug == 'videography-service' ||
      action?.applicableProduct?.slug == 'drone-footage-service'
        ? action?.applicableProduct
        : null,
    [action?.applicableProduct],
  );

  const unifiedCreditPurchaseDisabled =
    unifiedCreditsOnly &&
    !!action &&
    !action?.isSufficient &&
    selectedPaymentOption === 'credits';

  // Product quota: one unit per action. An agent at zero cannot pay, so confirming is blocked.
  const isQuotaAction = !!action?.isQuota;
  const quotaProductName = isQuotaAction ? t(getQuotaProductName(action?.applicableProduct) || '') : '';
  const quotaBlocked =
    isQuotaAction && (selectedPaymentOption === 'quota' ? !action?.isSufficient : !canBuyQuota(user));

  const showPaymentMethodSection =
    !!paymentOptions?.length &&
    (isQuotaAction ||
      unifiedCreditsOnly ||
      paymentOptions.length > 1 ||
      action?.isSufficient === true ||
      !user?.isCurrencyUser);

  const isUnifiedInfoMessage = unifiedCreditsOnly && isUnifiedPostListing;
  const isUnifiedRefreshOrBoostAction =
    unifiedCreditsOnly && ['refresh', 'boost-to-top'].includes(action?.applicableProduct?.slug);
  const showUnifiedListingExpiryUpdateMessage = isUnifiedInfoMessage && !isUnifiedRefreshOrBoostAction;

  const isUnifiedLowCreditsCreditsPayment =
    unifiedCreditsOnly &&
    user?.isCurrencyUser &&
    !action?.isSufficient &&
    selectedPaymentOption === 'credits';

  const hideUnifiedExpiryInfoWhenLowCredits =
    showUnifiedListingExpiryUpdateMessage && isUnifiedLowCreditsCreditsPayment;

  const unifiedListingExpiryDays = useMemo(() => {
    const slug = action?.applicableProduct?.slug;
    const isUnifiedElite = ['signature-listing', 'elite-listing'].includes(slug);
    // Elite expiry matches Featured (30d) until product defines a separate duration.
    return unifiedCreditsOnly && isUnifiedElite ? 10 : 30;
  }, [action?.applicableProduct?.slug, unifiedCreditsOnly]);


  const unifiedListingExpiryDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + unifiedListingExpiryDays);
    return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }, [locale, unifiedListingExpiryDays]);

  const unifiedSignatureNoticeEffectiveDate = useMemo(() => {
    return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date());
  }, [locale]);

  const renderDataTable = (label, tableClassName, data, columns) => {
    return (
      <>
        <div className="mb-8">{label}</div>
        <DataTable className={tableClassName} columns={columns} data={data} />
      </>
    );
  };

  return (
    <ConfirmationModal
      title={requestService && requestService?.title}
      key={action?.id}
      width={'708px'}
      ref={modalRef}
      okText={requestService && t('Request Now')}
      onSuccess={onSuccess}
      onCancel={onCancel}
      {...rest}
      okButtonProps={unifiedCreditPurchaseDisabled || quotaBlocked ? { disabled: true } : rest?.okButtonProps}
    >
      <>
        {showInfoMessage && (user?.isCurrencyUser || isQuotaAction) && !hideUnifiedExpiryInfoWhenLowCredits && (
          <>
            <Alert
              type="info"
              showIcon={!showUnifiedListingExpiryUpdateMessage}
              className={showUnifiedListingExpiryUpdateMessage ? 'mb-16' : 'mb-8'}
              style={showUnifiedListingExpiryUpdateMessage ? { background: '#DAECFB80' } : undefined}
              message={
                showUnifiedListingExpiryUpdateMessage ? (
                  <Flex align="center" gap="16px">
                    <ListingExpiryUpdateIcon />
                    <span>
                      {t('The expiry date of the listing will update to {{expiry_date}}', {
                        expiry_date: unifiedListingExpiryDate,
                      })}
                    </span>
                  </Flex>
                ) : (
                  t(
                    isUnifiedRefreshOrBoostAction
                      ? 'The listing will be refreshed and it will get higher visibility'
                      : action?.applicableProduct?.infoMessage,
                    { expiry_days: action?.expiryDays },
                  )
                )
              }
              isMobile={isMobile}
            />
            {/* {isUnifiedInfoMessage && (
              <Flex align="center" gap="8px" className="mb-16">
                <Icon icon="HiInformationCircle" size={14} color="#4F4F4F" />
                <span style={{ color: '#4F4F4F', lineHeight: '150%' }}>
                  {t(
                    'Kindly note that effective from {{effective_date}}, credits required for signature listings will be revised, please check your account for further details',
                    { effective_date: unifiedSignatureNoticeEffectiveDate },
                  )}
                </span>
              </Flex>
            )} */}
          </>
        )}
        {requestService && (
          <TenantComponents.ServiceOptions
            className={isMobile ? 'mb-16' : 'mb-24'}
            template={'initial'}
            gap="8px"
            ref={serviceDataRef}
          />
        )}
        {!hideDataTable &&
          !isQuotaAction &&
          renderDataTable(
            action?.applicableProduct?.actionType === 'credit'
              ? t('Following credits will be utilized')
              : t('Following quota will be utilized'),
            'mb-16',
            [
              {
                ...(action?.applicableProduct?.actionType && { product: action?.applicableProduct?.name }),
                available:
                  action?.applicableProduct?.actionType === 'credit'
                    ? Number(action?.available_credits).toFixed(2)
                    : Number(action?.available_quota).toFixed(2),
                used:
                  Number(
                    resolveCreditsRequired(
                      action?.applicableProduct?.credits_required,
                      action?.expiryDays || 30,
                      action?.applicableProduct?.default_expiry_days,
                    ) ?? 0,
                  ) ||
                  (action?.applicableProduct?.quantity_required
                    ? Number(action?.applicableProduct?.quantity_required?.toFixed(2))
                    : 0),
              },
            ],
            action?.applicableProduct?.actionType === 'credit' ? creditColumns(t) : quotaColumns(t),
          )}
      </>
      {showPaymentMethodSection && (
        <>
          <Flex justify="space-between" align="center" className="mb-8" wrap="wrap" gap="8px">
            <Text className="fw-700">{t('Payment Method')}</Text>
            {isQuotaAction && (
              <Text className="fw-700" style={{ marginInlineStart: 'auto' }}>
                <span className="text-muted fw-400 fz-12">
                  {t('{{product}} available', { product: quotaProductName })}{' '}
                </span>
                {action?.availableUnits}
              </Text>
            )}
            {!isQuotaAction &&
              (user?.isCurrencyUser || unifiedCreditsOnly) &&
              action?.available_credits != null &&
              !unifiedCreditPurchaseDisabled && (
                <Text className="fw-700" style={{ marginInlineStart: 'auto' }}>
                  <span className="text-muted fw-400 fz-12">
                    {unifiedCreditsOnly ? t('Available Credit') : t('Available Credits')}{' '}
                  </span>
                  {formatPrice(action?.available_credits)}
                </Text>
              )}
          </Flex>
          {isUnifiedLowCreditsCreditsPayment && (
            <div
              className="mb-12"
              style={{
                background: '#fff5f5',
                border: '1px solid #ffccc7',
                borderRadius: 8,
                padding: isMobile ? 12 : 12,
              }}
            >
              <Flex align="center" justify="space-between" gap={12} style={{ width: '100%' }}>
                <InsufficientCreditsAlertIcon
                  size={isMobile ? 22 : 24}
                  style={{ flexShrink: 0, display: 'block' }}
                  aria-hidden
                />
                <div
                  style={{
                    minWidth: isMobile ? 108 : 134,
                    maxWidth: isMobile ? '100%' : undefined,
                    borderRadius: 6,
                    background: '#fff',
                    border: '1px solid #ffccc7',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexWrap: 'nowrap',
                    gap: 6,
                    padding: '10px 14px',
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  <span
                    className="text-muted"
                    style={{
                      fontSize: 12,
                      lineHeight: 1.2,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {t('Available Credit')}
                  </span>
                  <span
                    className="text-primary"
                    style={{
                      fontSize: 12,
                      lineHeight: 1.2,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      flex: '1 1 auto',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {formatPrice(action?.available_credits)}
                  </span>
                </div>
              </Flex>
              <div style={{ marginTop: 6, textAlign: 'left' }}>
                <div className="fw-700" style={{ fontSize: isMobile ? 14 : 16, marginBottom: 6, color: '#434343' }}>
                  {t('Low Credit Balance')}
                </div>
                <div style={{ fontSize: isMobile ? 12 : 13, lineHeight: 1.5, color: '#595959' }}>
                  {t('low_credit_balance_unified_hint')}
                </div>
              </div>
            </div>
          )}
          <RadioGroup value={selectedPaymentOption} className="w-100">
            {paymentOptions?.map((e) => (
              <Card
                key={e?.key}
                as={selectedPaymentOption == e.key && !unifiedCreditPurchaseDisabled ? CardCompact : undefined}
                compactCardRadius="8px"
                compactCardPadding="12px"
                onClick={() => !unifiedCreditPurchaseDisabled && onChangePaymentOption(e)}
                style={{
                  borderWidth: 1,
                  opacity: unifiedCreditPurchaseDisabled || e?.disabled ? 0.65 : 1,
                  cursor: unifiedCreditPurchaseDisabled || e?.disabled ? 'not-allowed' : 'pointer',
                }}
                className={cx('mb-12', !unifiedCreditPurchaseDisabled && !e?.disabled && 'pointer')}
              >
                <Flex justify="space-between" align="center">
                  <RadioStyled
                    value={e.key}
                    className="radioInput"
                    disabled={unifiedCreditPurchaseDisabled || e?.disabled}
                  >
                    <TenantComponents.Product title={e?.title} description={e?.description} />
                  </RadioStyled>
                  <Flex align="center" gap="4px">
                    {unifiedCreditsOnly && e?.key === 'credits' ? (
                      <>
                        <span className="fz-12 text-muted">{t('Credits')}</span>
                        <span className="fw-700 fz-16 text-primary">{formatPrice(e.value)}</span>
                      </>
                    ) : (
                      <>
                        <div className={cx('fz-12', 'text-primary', 'text-muted')} style={{ display: 'inline-flex' }}>
                          {e.prefix}
                        </div>
                        <div className="fw-700 fz-16 text-primary">{e.value}</div>
                      </>
                    )}
                  </Flex>
                </Flex>
              </Card>
            ))}
          </RadioGroup>
        </>
      )}
      {isQuotaAction && !action?.isSufficient && !canBuyQuota(user) && (
        <Alert
          type="warning"
          showIcon
          message={t('You have no {{product}} left. Ask your agency admin to assign more.', {
            product: quotaProductName,
          })}
        />
      )}
      {showInsufficientCreditsAlert &&
        !isQuotaAction &&
        user?.isCurrencyUser &&
        !action?.isSufficient &&
        selectedPaymentOption == 'credits' &&
        !unifiedCreditsOnly && (
          <Alert
            style={{ padding: 0 }}
            showIcon
            type=""
            icon={<Icon className="color-gray-dark" icon="HiInformationCircle" size={16} />}
            message={
              <div
                className={cx('color-gray-light', isMobile ? 'fz-12' : 'fz-14')}
                style={{ paddingBlockStart: !isMobile && '3px', lineHeight: isMobile && 1.5 }}
              >
                <>
                  {t('Insufficient Credits')}
                  {'.'} {t('Pay')}{' '}
                  <strong className="color-primary">
                    {tenantConstants.CURRENCY_SYMBOL()}{' '}
                    {formatPrice(Math.round(action?.creditsDiff * action?.credit_unit_price))}
                  </strong>{' '}
                  {t(`for the additional {{credits}} credits at checkout`, { credits: action?.creditsDiff })}
                </>
              </div>
            }
          />
        )}
    </ConfirmationModal>
  );
});
